import { fromZonedTime } from 'date-fns-tz';
import { format } from 'date-fns/format';
import { lastValueFrom } from 'rxjs';

import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DriverService, StorageService, UserService } from '@core/services';
import { injectQuery, QueryClient } from '@tanstack/angular-query-experimental';

import { driverKeys } from './driver.keys';

export interface DriverRealtrip {
  id: number;
  bus_id?: number;
  active?: boolean;
  current_stop_id?: number | null;
  stop_id?: number;
  route_name?: string;
  route_type?: 'go' | 'return';
  route?: { type?: 'go' | 'return'; name?: string };
  plaque?: string;
  starts_at?: string;
  title?: string;
  formattedTime?: string;
  busticket_scans?: number;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class DriverStore {
  private driverService = inject(DriverService);
  private storage = inject(StorageService);
  private queryClient = inject(QueryClient);
  private userService = inject(UserService);

  // Reactive auth gate: these queries hit driver-only endpoints, so they must
  // stay disabled until a driver is actually logged in. Without this, the store
  // (provided in root and eagerly created by AuthService) fired `transportec_realtrip`
  // at app bootstrap — before login — and got a 401.
  private currentUser = toSignal(this.userService.user$, { initialValue: null });
  private isDriver = computed(() => this.currentUser()?.type2 === 'transportec-driver');

  realtripId = signal<number | null>(null);

  nextRealtripQuery = injectQuery(() => ({
    queryKey: driverKeys.realtrip(),
    queryFn: () => lastValueFrom(this.driverService.getNextRealtrip()),
    enabled: this.isDriver() && !this.realtripId(),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  }));

  realtripDetailQuery = injectQuery(() => ({
    queryKey: driverKeys.realtripDetail(this.realtripId()!),
    queryFn: () => lastValueFrom(this.driverService.getRealtripById(this.realtripId()!)),
    enabled: this.isDriver() && !!this.realtripId(),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  }));

  realtrip = computed<DriverRealtrip | null>(() => {
    let raw: any = null;

    if (this.realtripId()) {
      raw = this.realtripDetailQuery.data()?.data;
    } else {
      raw = this.nextRealtripQuery.data()?.realtrip;
    }

    if (!raw?.id) return null;

    const time = this.#formatZoneDate(raw.starts_at);
    return {
      ...raw,
      title: raw.route_name + ' - ' + time,
      formattedTime: time,
    };
  });

  isLoading = computed(() => {
    if (this.realtripId()) return this.realtripDetailQuery.isLoading();
    return this.nextRealtripQuery.isLoading();
  });

  constructor() {
    // Sync realtrip to storage and auto-set realtripId from nextRealtripQuery
    effect(() => {
      const rt = this.realtrip();
      if (rt?.id) {
        this.storage.set('realtrip', rt);
        // Once we know the ID from the "next" query, set it so future refreshes use detail endpoint
        if (!this.realtripId()) {
          this.realtripId.set(rt.id);
        }
      }
    });
  }

  reset() {
    this.realtripId.set(null);
  }

  setRealtripId(id: number) {
    this.realtripId.set(id);
  }

  invalidateRealtrip() {
    this.queryClient.invalidateQueries({ queryKey: driverKeys.realtrip() });
    if (this.realtripId()) {
      this.queryClient.invalidateQueries({ queryKey: driverKeys.realtripDetail(this.realtripId()!) });
    }
  }

  invalidateAll() {
    this.queryClient.invalidateQueries({ queryKey: driverKeys.all });
  }

  #formatZoneDate(date: string, stringFormat: string = 'HH:mm', zone: string = 'America/Mexico_City') {
    const zonedDate = fromZonedTime(date, zone);
    return format(zonedDate, stringFormat);
  }
}
